import { useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../services/ApiService';
import { Imovel, PropertySearchFilters, PropertySearchResponse } from '../types/Property';

export const useProperties = (initialFilters: PropertySearchFilters = {}) => {
  const [imoveis, setImoveis] = useState<Imovel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState<PropertySearchFilters>(initialFilters);
  // Flag para controlar se devemos fazer busca automática na inicialização
  const skipInitialSearch = Object.keys(initialFilters).length === 0;
  // Ref para rastrear se uma busca manual foi feita (para evitar busca automática duplicada)
  const manualSearchRef = useRef(false);
  // Ref para manter os filtros atuais sem causar recriação do callback
  const filtersRef = useRef(filters);
  // Ref para rastrear se já foi feita a primeira busca (para evitar busca automática na inicialização)
  const hasSearchedRef = useRef(false);
  
  // Atualizar ref quando filters mudar
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const searchImoveis = useCallback(async (searchFilters: PropertySearchFilters = {}) => {
    setLoading(true);
    setError(null);

    try {
      // Se searchFilters tem propriedades, usar apenas eles (ignorar filters internos)
      // Isso evita que filtros antigos sejam mesclados com os novos
      const hasExplicitFilters = Object.keys(searchFilters).length > 0;
      const filtersToUse = hasExplicitFilters 
        ? searchFilters 
        : { ...filtersRef.current, ...searchFilters };
      
      // Se passamos filtros explícitos, marcar que foi uma busca manual
      // NÃO atualizar os filtros internos aqui para evitar recriar o searchImoveis
      // e disparar o useEffect novamente
      if (hasExplicitFilters) {
        manualSearchRef.current = true;
      }
      
      const response = await apiService.getImoveis(filtersToUse);

      if (response.success && response.data) {
        const data = response.data as PropertySearchResponse;
        console.log('🏠 Imóveis encontrados:', { 
          total: data.imoveis?.length || 0,
          pagination: data.pagination,
          imoveisComAluguel: data.imoveis?.filter(i => i.valor_aluguel && i.valor_aluguel > 0).length || 0,
          imoveisComVenda: data.imoveis?.filter(i => i.valor_venda && i.valor_venda > 0).length || 0,
          imoveisComAmbos: data.imoveis?.filter(i => (i.valor_aluguel && i.valor_aluguel > 0) && (i.valor_venda && i.valor_venda > 0)).length || 0
        });
        setImoveis(data.imoveis || []);
        setPagination(data.pagination || { page: 1, limit: 100, total: 0, totalPages: 0 });
      } else {
        console.warn('⚠️ Resposta da API sem sucesso:', response);
        setError(response.message || 'Erro ao buscar imóveis');
        setImoveis([]);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao buscar imóveis');
    } finally {
      setLoading(false);
    }
  }, []); // Remover dependência de filters para evitar recriação desnecessária

  const updateFilters = useCallback((newFilters: Partial<PropertySearchFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  const loadPage = useCallback((page: number) => {
    searchImoveis({ ...filters, page });
  }, [searchImoveis, filters]);

  // Buscar imóveis quando os filtros mudarem
  // Mas pular a busca inicial se skipInitialSearch estiver ativo
  // E pular se uma busca manual foi feita recentemente
  useEffect(() => {
    // Se pulamos a busca inicial, não buscar na primeira vez
    if (skipInitialSearch && !hasSearchedRef.current) {
      hasSearchedRef.current = true;
      return;
    }
    
    // Se uma busca manual foi feita, não fazer busca automática
    if (manualSearchRef.current) {
      // Resetar a flag após um delay para permitir buscas futuras
      const timeoutId = setTimeout(() => {
        manualSearchRef.current = false;
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
    
    // NUNCA fazer busca automática se skipInitialSearch foi true (ou seja, quando inicializamos sem filtros)
    // Isso evita buscas automáticas quando há searchData
    if (skipInitialSearch) {
      return;
    }
    
    // Só fazer busca automática se houver filtros e ainda não buscamos
    if (Object.keys(filtersRef.current).length > 0 && !hasSearchedRef.current) {
      hasSearchedRef.current = true;
      searchImoveis();
    }
  }, [searchImoveis, skipInitialSearch]);

  return {
    imoveis,
    loading,
    error,
    pagination,
    filters,
    searchImoveis,
    updateFilters,
    resetFilters,
    loadPage,
  };
};

export const useProperty = (id: string | null) => {
  const [imovel, setImovel] = useState<Imovel | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchImovel = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.getImovel(id);

      if (response.success && response.data) {
        setImovel(response.data);
      } else {
        setError(response.message || 'Erro ao buscar imóvel');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao buscar imóvel');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchImovel();
  }, [fetchImovel]);

  return {
    imovel,
    loading,
    error,
    refetch: fetchImovel,
  };
};

export const useMyProperties = () => {
  const [imoveis, setImoveis] = useState<Imovel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMyProperties = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      console.log('useProperties - Token no localStorage:', token ? 'Presente' : 'Ausente');
      console.log('useProperties - Fazendo requisição para /imoveis/meus');
      const response = await apiService.getMyImoveis();
      console.log('useProperties - Resposta recebida:', response);

      if (response.success && response.data) {
        // O backend retorna { data: { imoveis: [], total: number } }
        const imoveis = response.data.imoveis || response.data;
        setImoveis(imoveis);
        console.log('useProperties - Imóveis definidos:', imoveis);
      } else {
        setError(response.message || 'Erro ao buscar meus imóveis');
        console.error('useProperties - Erro:', response.message);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao buscar meus imóveis');
      console.error('useProperties - Erro na requisição:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createProperty = useCallback(async (propertyData: any) => {
    try {
      const response = await apiService.createImovel(propertyData);
      
      if (response.success) {
        // Recarregar a lista após criar
        await fetchMyProperties();
        return { success: true, message: response.message, data: response.data };
      } else {
        return { success: false, message: response.message || 'Erro ao criar imóvel' };
      }
    } catch (err: any) {
      return { success: false, message: err.message || 'Erro ao criar imóvel' };
    }
  }, [fetchMyProperties]);

  const updateProperty = useCallback(async (id: string, propertyData: any) => {
    try {
      const response = await apiService.updateImovel(id, propertyData);
      
      if (response.success) {
        // Recarregar a lista após atualizar
        await fetchMyProperties();
        return { success: true, message: response.message };
      } else {
        return { success: false, message: response.message || 'Erro ao atualizar imóvel' };
      }
    } catch (err: any) {
      return { success: false, message: err.message || 'Erro ao atualizar imóvel' };
    }
  }, [fetchMyProperties]);

  const deleteProperty = useCallback(async (id: string) => {
    try {
      const response = await apiService.deleteImovel(id);
      
      if (response.success) {
        // Recarregar a lista após deletar
        await fetchMyProperties();
        return { success: true, message: response.message };
      } else {
        return { success: false, message: response.message || 'Erro ao deletar imóvel' };
      }
    } catch (err: any) {
      return { success: false, message: err.message || 'Erro ao deletar imóvel' };
    }
  }, [fetchMyProperties]);

  const uploadPropertyPhotos = useCallback(async (imovelId: string, files: File[]) => {
    try {
      const response = await apiService.uploadMidias(imovelId, files);
      
      if (response.success) {
        // Recarregar a lista após upload
        await fetchMyProperties();
        return { success: true, message: response.message, data: response.data };
      } else {
        return { success: false, message: response.message || 'Erro ao fazer upload das fotos' };
      }
    } catch (err: any) {
      return { success: false, message: err.message || 'Erro ao fazer upload das fotos' };
    }
  }, [fetchMyProperties]);

  useEffect(() => {
    fetchMyProperties();
  }, [fetchMyProperties]);

  return {
    imoveis,
    loading,
    error,
    fetchMyProperties,
    createProperty,
    updateProperty,
    deleteProperty,
    uploadPropertyPhotos,
  };
};
